import styles from "../../../styles/components/Modal/Employees/AddDeskModal.module.css";
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { SideBar } from "./components/SideBar";
import { PersonalData } from "./components/PersonalData";
import { PermissionGroupTab } from "./components/NewUser/PermissionGroupTab";
import { addAdmin } from '../../../utils/api/Admin/addAdminApi';
import { getRoles } from "../../../utils/api/Admin/getRoles";
import { getAllDesksApi } from "../../../utils/api/Admin/desk/getAllDesksApi";
import { getPermissions } from "../../../utils/api/Admin/Permissions/getPermissionsApi";
import { permissionTranslations } from '../../../utils/translations/permissionTranslations';
import { syncAdminPermissions } from "../../../utils/api/Admin/Permissions/syncAdminPermissionsApi";
import { handleError } from "../../../utils/custom/errorHandler";
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { MultiSelectDropdown } from '../../UI/MultiSelectDropdown';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { CommonPermissionGroupsTab } from './components/NewUser/CommonPermissionGroupsTab';
import { COMMON_PERMISSIONS_TAB_KEY, getAvailableCommonPermissionGroups } from '../../../utils/commonPermissionGroups';
import { isTelegramTagValid } from '../../../utils/validation/telegramTag';
import { LeadFieldAccessTab } from './components/NewUser/LeadFieldAccessTab';
import {
    createDefaultLeadFieldAccessModes,
    hasCustomLeadFieldAccessModes,
    LEAD_FIELD_ACCESS_TAB_KEY,
    getLeadFieldAccessSections,
} from '../../../utils/leadFieldAccess';

const translations = {
    ru: {
        loadingData: "Загрузка данных...",
        loadingMessage: "Данные загружаются, пожалуйста подождите...",
        addUser: "Добавить сотрудника",
        personalDataTab: "Персональные данные",
        commonPermissions: "Общие",
        leadFieldAccess: "Поля лида",
        generalError: "Ошибка при загрузке данных: ",
        saving: "Добавление...",
        invalidTelegramTag: "Telegram должен быть в формате @user_tag",
    },
    en: {
        loadingData: "Loading data...",
        loadingMessage: "Data is loading, please wait...",
        addUser: "Add Employee",
        personalDataTab: "Personal Data",
        commonPermissions: "Common",
        leadFieldAccess: "Lead Fields",
        generalError: "Error loading data: ",
        saving: "Adding...",
        invalidTelegramTag: "Telegram must match the format @user_tag",
    },

};

export const AddUserModal = ({ handlenewUserModal, onUserAdded, permissions = {} }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const t = translations[langSelected] || translations.ru;
    const pT = permissionTranslations;

    const [activeTab, setActiveTab] = useState('personalData');
    const [newUserData, setNewUserData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        telegram_tag: '',
        password: '',
        desk_ids: [],
        role: null,
        permissions: [],
        lead_field_access: createDefaultLeadFieldAccessModes(),
    });
    const [rolesData, setRolesData] = useState([]);
    const [desksData, setDesksData] = useState([]);
    const [roleOptionsForDropdown, setRoleOptionsForDropdown] = useState([]);
    const [deskOptionsForDropdown, setDeskOptionsForDropdown] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const modalRef = useRef(null);

    const [allPermissions, setAllPermissions] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState({});
    const [commonPermissionGroups, setCommonPermissionGroups] = useState([]);
    const [leadFieldAccessSections, setLeadFieldAccessSections] = useState([]);
    const [permissionGroupDisplayNames, setPermissionGroupDisplayNames] = useState({});
    const [availableTabs, setAvailableTabs] = useState(['personalData']);
    const canManageAdminPermissions = Boolean(permissions['user.change_admin_permissions']);

    const handleClickOutside = useCallback((event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            handlenewUserModal();
        }
    }, [handlenewUserModal]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);



    const clearValidationError = useCallback((field) => {
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors[field]) {
                delete newErrors[field];
            }
            return newErrors;
        });
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setValidationErrors({});

            try {
                const [roles, desks, permissions] = await Promise.all([
                    getRoles(),
                    getAllDesksApi(1),
                    getPermissions()
                ]);

                setRolesData(roles.data);
                setRoleOptionsForDropdown(roles.data.map(role => ({ value: role.id, label: role.name })));

                setDesksData(desks.data);
                setDeskOptionsForDropdown(desks.data.map(desk => ({ value: desk.id, label: desk.name, description: desk.description })));

                setAllPermissions(permissions.data);
                const availableCommonGroups = getAvailableCommonPermissionGroups(permissions.data);
                setCommonPermissionGroups(availableCommonGroups);
                setLeadFieldAccessSections(getLeadFieldAccessSections(langSelected));

                const permissionsByGroup = permissions.data.reduce((acc, permission) => {
                    const groupName = permission.group || "Без группы";
                    if (!acc[groupName]) {
                        acc[groupName] = [];
                    }
                    acc[groupName].push(permission);
                    return acc;
                }, {});
                setGroupedPermissions(permissionsByGroup);

                const groupDisplayNames = {
                    personalData: t.personalDataTab,
                    [COMMON_PERMISSIONS_TAB_KEY]: t.commonPermissions,
                    [LEAD_FIELD_ACCESS_TAB_KEY]: t.leadFieldAccess,
                };
                Object.keys(permissionsByGroup).forEach(group => {
                    groupDisplayNames[group] = pT.groups[group]?.[langSelected] || permissionsByGroup[group][0]?.group_display_name || group;
                });
                setPermissionGroupDisplayNames(groupDisplayNames);

                const dynamicTabs = Object.keys(permissionsByGroup).sort((a, b) => {
                    const nameA = pT.groups[a]?.[langSelected] || a;
                    const nameB = pT.groups[b]?.[langSelected] || b;
                    return nameA.localeCompare(nameB);
                });
                setAvailableTabs([
                    'personalData',
                    ...(availableCommonGroups.length > 0 ? [COMMON_PERMISSIONS_TAB_KEY] : []),
                    ...(canManageAdminPermissions ? [LEAD_FIELD_ACCESS_TAB_KEY] : []),
                    ...dynamicTabs
                ]);

            } catch (err) {
                console.error(t.generalError, err.message);
                handleError(err.response?.data || err, setValidationErrors);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [t.personalDataTab, t.commonPermissions, t.leadFieldAccess, t.generalError, pT, langSelected, canManageAdminPermissions]);

    const handlePersonalDataChange = useCallback((data) => {
        setNewUserData(prev => ({ ...prev, ...data }));
        if (data.first_name !== undefined) clearValidationError('first_name');
        if (data.last_name !== undefined) clearValidationError('last_name');
        if (data.email !== undefined) clearValidationError('email');
        if (data.telegram_tag !== undefined) clearValidationError('telegram_tag');
        if (data.password !== undefined) clearValidationError('password');
    }, [clearValidationError]);

    const handleRoleChange = useCallback((selectedRoleId) => {
        setNewUserData(prev => ({ ...prev, role: selectedRoleId }));
        clearValidationError('role');
    }, [clearValidationError]);

    const handleDeskChange = useCallback((filterName, selectedDeskIdsArray, filterKey) => {
        setNewUserData(prev => ({ ...prev, desk_ids: selectedDeskIdsArray }));
        clearValidationError('desk_ids');
    }, [clearValidationError]);

    const handlePermissionChange = useCallback((permissionName, isChecked) => {
        setNewUserData(prevData => {
            const currentPermissions = prevData.permissions;
            let updatedPermissions;
            if (isChecked) {
                updatedPermissions = [...new Set([...currentPermissions, permissionName])];
            } else {
                updatedPermissions = currentPermissions.filter(name => name !== permissionName);
            }
            return { ...prevData, permissions: updatedPermissions };
        });
    }, []);

    const handlePermissionDropdownChange = useCallback((filterName, selectedValue, filterKey) => {
        setNewUserData(prevData => {
            const currentPermissions = new Set(prevData.permissions);

            allPermissions.forEach(p => {
                if (p.name.startsWith(filterKey + '.')) {
                    currentPermissions.delete(p.name);
                }
            });

            if (selectedValue) {
                currentPermissions.add(selectedValue);
            }
            return { ...prevData, permissions: Array.from(currentPermissions) };
        });
    }, [allPermissions]);

    const handleCommonPermissionGroupToggle = useCallback((permissionNames, isEnabled) => {
        setNewUserData((prevData) => {
            const nextPermissions = new Set(prevData.permissions);

            permissionNames.forEach((permissionName) => {
                if (isEnabled) {
                    nextPermissions.add(permissionName);
                } else {
                    nextPermissions.delete(permissionName);
                }
            });

            return {
                ...prevData,
                permissions: Array.from(nextPermissions),
            };
        });
    }, []);

    const handleLeadFieldAccessChange = useCallback((fieldKey, nextMode) => {
        setNewUserData((prevData) => ({
            ...prevData,
            lead_field_access: {
                ...prevData.lead_field_access,
                [fieldKey]: nextMode,
            },
        }));
    }, []);


    const handleSubmit = async () => {
        setIsLoading(true);
        setValidationErrors({});

        if (!isTelegramTagValid(newUserData.telegram_tag)) {
            setValidationErrors({ telegram_tag: t.invalidTelegramTag });
            setActiveTab('personalData');
            setIsLoading(false);
            return;
        }

        const dataToSend = { ...newUserData };
        
        if (dataToSend.desk_ids && Array.isArray(dataToSend.desk_ids)) {
        } else {
            delete dataToSend.desk_ids;
        }

        if (dataToSend.role !== null && dataToSend.role !== '') {
            dataToSend.role = parseInt(dataToSend.role, 10);
        } else {
            delete dataToSend.role;
        }
        
        const { permissions, ...userDataForCreate } = dataToSend;

        try {
            const addAdminResponse = await addAdmin(userDataForCreate);
            const newAdminId = addAdminResponse.id || addAdminResponse.user?.id;

            if (!newAdminId) {
                throw new Error("Не удалось получить ID нового администратора после создания. Проверьте структуру ответа API.");
            }

            if (newUserData.permissions.length > 0 || hasCustomLeadFieldAccessModes(newUserData.lead_field_access)) {
                await syncAdminPermissions(newAdminId, newUserData.permissions, newUserData.lead_field_access);
            }

            setIsLoading(false);
            onUserAdded();

        } catch (err) {
            console.error("Ошибка в процессе создания или синхронизации администратора:", err);
            setIsLoading(false);

            handleError(err.response?.data || err, setValidationErrors);

            const errorsExist = Object.keys(err.response?.data?.errors || {}).length > 0 || err.response?.data?.message;
            if (errorsExist) {
                setActiveTab('personalData');
            }
        }
    };

    if (isLoading) {
        return (
            <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
                <div className={`${styles.filtersContainer} ${styles.employeeFiltersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                    <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                        <p className={`${styles.modalLabelText} ${isDark ? styles.darkModalLabelText : ''}`}>{t.loadingData}</p>
                        <div className={`${styles.modalLabelCloseButton} ${isDark ? styles.darkModalLabelCloseButton : ''}`} onClick={handlenewUserModal}>
                            <CloseIcon />
                        </div>
                    </div>
                    <div className={`${styles.modalContent} ${isDark ? styles.darkModalContent : ''}`} style={{ justifyContent: 'center', alignItems: 'center' }}>
                        <p>{t.loadingMessage}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.modal} ${isDark ? styles.darkModal : ''}`}>
            <div className={`${styles.filtersContainer} ${styles.employeeFiltersContainer} ${isDark ? styles.darkFiltersContainer : ''}`} ref={modalRef}>
                <div className={`${styles.modalLabel} ${isDark ? styles.darkModalLabel : ''}`}>
                    <div className={styles.modalLabelTextContainer}>
                        <p className={`${styles.modalLabelText} ${isDark ? styles.darkModalLabelText : ''}`}>{t.addUser}</p>
                    </div>
                    <div className={styles.modalLabelCloseButtonContainer}>
                        <div className={`${styles.modalLabelCloseButton} ${isDark ? styles.darkModalLabelCloseButton : ''}`} onClick={handlenewUserModal}>
                            <CloseIcon />
                        </div>
                    </div>
                </div>
                <div className={styles.modalContent}>
                    <div className={`${styles.leftPanel} ${isDark ? styles.darkLeftPanel : ''}`}>
                        <SideBar
                            setActiveTab={setActiveTab}
                            activeTab={activeTab}
                            tabs={availableTabs}
                            permissionGroupDisplayNames={permissionGroupDisplayNames}
                            isDark={isDark}
                        />
                    </div>
                    <div className={`${styles.section} ${isDark ? styles.darkSection : ''}`}>
                        {validationErrors.general && (
                            <div className={`${styles.generalError} ${isDark ? styles.darkGeneralError : ''}`}>
                                {validationErrors.general}
                            </div>
                        )}

                        {activeTab === 'personalData' && (
                            <PersonalData
                                userInfo={newUserData}
                                setUserInfo={handlePersonalDataChange}
                                roleOptions={roleOptionsForDropdown}
                                onRoleChange={handleRoleChange}
                                deskOptions={deskOptionsForDropdown}
                                onDeskChange={handleDeskChange}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                isNewUser={true}
                                selectedDeskIds={newUserData.desk_ids}
                                DropdownComponent={DropdownSelector}
                                MultiSelectComponent={MultiSelectDropdown}
                                isDark={isDark}
                            />
                        )}

                        {activeTab === COMMON_PERMISSIONS_TAB_KEY && commonPermissionGroups.length > 0 && (
                            <CommonPermissionGroupsTab
                                groups={commonPermissionGroups}
                                selectedPermissions={newUserData.permissions}
                                onToggleGroup={handleCommonPermissionGroupToggle}
                                groupedPermissions={groupedPermissions}
                                isDark={isDark}
                            />
                        )}

                        {activeTab === LEAD_FIELD_ACCESS_TAB_KEY && canManageAdminPermissions && (
                            <LeadFieldAccessTab
                                sections={leadFieldAccessSections}
                                leadFieldAccess={newUserData.lead_field_access}
                                onLeadFieldAccessChange={handleLeadFieldAccessChange}
                            />
                        )}

                        {Object.keys(groupedPermissions).map(groupName => (
                            activeTab === groupName && (
                                <PermissionGroupTab
                                    key={groupName}
                                    groupName={groupName}
                                    groupDisplayName={permissionGroupDisplayNames[groupName]}
                                    permissions={groupedPermissions[groupName]}
                                    selectedPermissions={newUserData.permissions}
                                    handlePermissionChange={handlePermissionChange}
                                    handlePermissionDropdownChange={handlePermissionDropdownChange}
                                    isDark={isDark}
                                />
                            )
                        ))}
                    </div>
                </div>
                <div className={`${styles.modalFooter} ${isDark ? styles.darkModalFooter : ''}`}>
                    <div className={styles.confirmationWrapper}>
                        {isLoading ? (
                            <button className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`} disabled>{t.saving}</button>
                        ) : (
                            <button
                                className={`${styles.submitButton} ${isDark ? styles.darkSubmitButton : ''}`}
                                onClick={handleSubmit}
                                disabled={isLoading}
                            >
                                {t.addUser}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
